import type { SyntheticEvent } from 'react'
import { model as router } from '!/router'
import { register } from '!/repository'
import { attach, createEvent, restore, sample, type Effect } from 'effector'
import { $isLockedFn, $unlockedId, unlockId } from './content'
import {
  emptyPinLockState,
  openPinLockPopup,
  type IPinLockPopupProps,
} from './popup/model'

type InterceptParams = {
  id?: number
  locked?: boolean
  name?: string
  '<event>': SyntheticEvent
}

type InterceptResult = {
  id?: number
  locked?: boolean
  name?: string
  href?: string | null
}

/**
 * Event to intercept content interactions that may require PIN lock validation
 */
export const intercept = createEvent<InterceptParams>()

/**
 * Effect to handle the interception logic, checking if content is locked
 * and returning the appropriate result or throwing an error to trigger the popup
 */
const interceptFx = attach({
  source: $isLockedFn,
  effect(isLocked, { '<event>': event, ...content }: InterceptParams) {
    const locked = isLocked(content)
    const prevented = event.defaultPrevented
    const target = event.target

    // biome-ignore format: I want this to be in a single line
    // eslint-disable-next-line no-nested-ternary
    const link = target instanceof HTMLElement ? (target instanceof HTMLAnchorElement ? target : target.closest('a')) : null
    const href = link?.getAttribute('href')

    //
    // if content is locked
    //

    if (locked) {
      // if event is not prevented, we need to prevent default action,
      // to stop the browser from navigating to the link
      if (!prevented) {
        event.preventDefault()
        event.stopPropagation()
      }

      // throw in order to branch the flow to the popup,
      // include the content data and the href to navigate to after successful unlock
      throw {
        id: content.id,
        name: content.name,
        locked,
        href,
      }
    }

    //
    // if content is not locked
    //

    // if event is not prevented - just return nothing,
    // so the browser will navigate to the link as usual
    if (!prevented) {
      return
    }

    // otherwise return the content data with the href to navigate to,
    // in order to navigate to the link manually
    return {
      id: content.id,
      name: content.name,
      locked,
      href,
    }
  },
}) as Effect<InterceptParams, InterceptResult | undefined, InterceptResult>

sample({
  clock: intercept,
  target: interceptFx,
})

//
// Opened flow branch
//

sample({
  clock: interceptFx.doneData,
  filter: (result) => typeof result?.href === 'string',
  fn: (result) => result!.href!,
  target: router.navigatePush,
})

//
// Locked flow branch
//

sample({
  clock: interceptFx.failData,
  filter: (result) => typeof result?.href === 'string',
  fn: ({ id, name, locked }) =>
    ({
      ...emptyPinLockState,
      id,
      name,
      locked,
    }) as IPinLockPopupProps,
  target: openPinLockPopup,
})

sample({
  clock: unlockId,
  source: {
    unlockedId: $unlockedId,
    result: restore(interceptFx.failData, null).reset(interceptFx),
  },
  filter: ({ result, unlockedId }) =>
    typeof result?.href === 'string' && result.id === unlockedId,
  fn: ({ result }) => result!.href!,
  target: router.navigatePush,
})

//
//
//

register(intercept, 'features/pin-lock/intercept')
